<div class="tm-chart" *ngIf="data">
  <div #tmHost [id]="$any(data).widgetId" class="tm-host" [style.height]="hostHeight">

    <!-- Drill-Down type → delegate to the proven lib-drill-down tree renderer. -->
    <lib-drill-down *ngIf="isDrilldown && drilldownData" [data]="drilldownData" (save)="onDrilldownSave($event)" [class.dd-no-col-lines]="!showColumnLines" [style.--dd-header-font-size]="headerFontSizeCss" [style.fontFamily]="fontFamily" [style.fontSize.px]="fontSizeOverride"></lib-drill-down>
    <div class="tm-empty" *ngIf="isDrilldown && !drilldownData">Map hierarchy dimensions + measures to build the drill-down table.</div>

    <!-- Normal / Editable → self-rendered HTML table. -->
    <ng-container *ngIf="!isDrilldown">
      <!-- Toolbar -->
      <div class="tm-toolbar" *ngIf="showInlineViewToggle || enableSearch || enableColumnSearch || enableColumnReorder || enableColumnResize || enableExport || enableExcelExport || enablePdfExport || enablePrint || enableRefresh || enableSavePrefs || enableUpload">
        <!-- Loading is shown ON the toggle (dimmed + progress bar) rather than
             as an adjacent text node, which would reflow the toolbar on every
             switch. The error is an icon of fixed width for the same reason. -->
        <div class="tm-view-toggle" *ngIf="showInlineViewToggle" [class.is-loading]="toggleViewLoading" role="group" [attr.aria-label]="'Table view'" [attr.aria-busy]="toggleViewLoading">
          <button type="button" class="tm-btn" [class.tm-btn-active]="activeToggleView === 1" [disabled]="toggleViewLoading" (click)="setToggleView(1)" [title]="toggleViewLabels[0]">{{ toggleViewLabels[0] }}</button>
          <button type="button" class="tm-btn" [class.tm-btn-active]="activeToggleView === 2" [disabled]="toggleViewLoading" (click)="setToggleView(2)" [title]="toggleViewLabels[1]">{{ toggleViewLabels[1] }}</button>
        </div>
        <span class="tm-view-toggle-error" *ngIf="showInlineViewToggle" [class.is-visible]="!toggleViewLoading && toggleViewError" role="alert" [title]="toggleViewError">⚠</span>
        <input
          *ngIf="enableSearch"
          class="tm-search"
          type="text"
          [ngModel]="searchText"
          (ngModelChange)="searchText = $event; pageIndex = 0"
          placeholder="Search…"
        />
        <button *ngIf="enableColumnSearch" type="button" class="tm-btn" [class.tm-btn-active]="showColumnSearch" (click)="toggleColumnSearch()" title="Toggle per-column search">⌕ Columns</button>
        <div class="tm-toolbar-spacer"></div>
        <!-- File upload → compute API → table re-renders. -->
        <ng-container *ngIf="enableUpload">
          <input #tmFileInput type="file" [accept]="uploadAccept" hidden (change)="onFileSelected($event)" />
          <button type="button" class="tm-btn tm-upload-btn" [disabled]="uploading" (click)="triggerUpload()" [title]="uploadMessage || 'Upload a file'">
            {{ uploading ? '⏳ Uploading…' : '⭱ ' + uploadButtonLabel }}
          </button>
          <span class="tm-upload-msg" *ngIf="uploadMessage && !uploading" [title]="uploadMessage">{{ uploadMessage }}</span>
        </ng-container>
        <button *ngIf="enableRefresh" type="button" class="tm-btn" (click)="onRefresh()" title="Refresh data">⟳</button>
        <button *ngIf="enableSavePrefs" type="button" class="tm-btn" (click)="saveUserPrefs()" title="Save my view (columns, sort, page)">☆ Save view</button>
        <!-- Column show/hide is CONFIG-DRIVEN only (each column's `hidden` flag);
             the end-user Columns menu was removed deliberately. -->
        <button *ngIf="enableColumnReorder || enableColumnResize" type="button" class="tm-btn" (click)="resetColumns()" title="Reset column order and widths">Reset columns</button>
        <div class="tm-export-menu" *ngIf="enableExport || enableExcelExport || enablePdfExport || enablePrint">
          <details>
            <summary title="Export / print">⭳ Export</summary>
            <div class="tm-export-list">
              <button *ngIf="enableExport" type="button" class="tm-btn" (click)="exportCsv()">CSV</button>
              <button *ngIf="enableExcelExport" type="button" class="tm-btn" (click)="exportExcel()">Excel</button>
              <button *ngIf="enablePdfExport" type="button" class="tm-btn" (click)="exportPdf()">PDF</button>
              <button *ngIf="enablePrint" type="button" class="tm-btn" (click)="printTable()">Print</button>
            </div>
          </details>
        </div>
      </div>

      <!-- Editable (Application Table) action bar: row ops + change tracking. -->
      <div class="tm-edit-toolbar" *ngIf="showEditToolbar">
        <button *ngIf="enableRowAdd" type="button" class="tm-btn tm-edit-add" (click)="addRow()" title="Add a new row">＋ Add row</button>
        <button *ngIf="enableUndoRedo" type="button" class="tm-btn" (click)="undo()" [disabled]="!canUndo()" title="Undo">↶ Undo</button>
        <button *ngIf="enableUndoRedo" type="button" class="tm-btn" (click)="redo()" [disabled]="!canRedo()" title="Redo">↷ Redo</button>
        <div class="tm-toolbar-spacer"></div>
        <span class="tm-dirty-summary" *ngIf="isDirty">
          <span *ngIf="dirtyCounts.new" class="tm-chip tm-chip-new">{{ dirtyCounts.new }} new</span>
          <span *ngIf="dirtyCounts.modified" class="tm-chip tm-chip-mod">{{ dirtyCounts.modified }} modified</span>
          <span *ngIf="dirtyCounts.deleted" class="tm-chip tm-chip-del">{{ dirtyCounts.deleted }} deleted</span>
        </span>
        <span class="tm-chip tm-chip-err" *ngIf="errorRowCount">⚠ {{ errorRowCount }} row(s) with errors</span>
        <span class="tm-chip tm-chip-auto" *ngIf="enableAutoSave" title="Edits save automatically">⟳ Auto-save</span>
        <!-- Workflow actions (act on the selected rows). -->
        <ng-container *ngIf="enableWorkflow">
          <button *ngFor="let act of workflowActions" type="button" class="tm-btn tm-wf-btn" (click)="runWorkflow(act)" [title]="'Workflow: ' + workflowLabel(act)">{{ workflowLabel(act) }}</button>
        </ng-container>
        <!-- Only surface Save/Cancel while there ARE unsaved changes — after a
             save (or cancel) settles the state they disappear again. -->
        <button *ngIf="!enableAutoSave && isDirty" type="button" class="tm-btn" (click)="cancelChanges()" title="Discard all changes">Cancel</button>
        <button *ngIf="showSaveAllButton" type="button" class="tm-btn tm-edit-save" (click)="saveAllRows()" [disabled]="errorRowCount > 0" title="Save all changes">Save all</button>
      </div>

      <!-- Row checkboxes: how many are ticked, with a way to clear them. -->
      <div class="tm-selection-bar" *ngIf="rowCheckbox && selectedCount" role="status">
        <span class="tm-chip tm-chip-selected">{{ selectedCount }} selected</span>
        <button type="button" class="tm-btn" (click)="clearSelection()" title="Clear the selection">Clear</button>
      </div>

      <div class="tm-empty" *ngIf="model.empty">Map columns to build the table.</div>

      <div class="tm-scroll" *ngIf="!model.empty" [class.tm-scroll-virtual]="virtualScroll || freezeRowCount > 0">
        <table
          class="tm-table"
          [class.tm-sticky-footer]="stickyFooter"
          [class.tm-striped]="striped"
          [class.tm-no-col-lines]="!showColumnLines"
          [class.tm-sticky-header]="stickyHeader"
          [style.fontSize.px]="fontSize"
          [style.fontFamily]="fontFamily"
          [style.--tm-cell-font-size]="cellFontSizeVar"
          [style.--tm-header-height]="headerHeightVar"
          [style.tableLayout]="layoutLocked ? 'fixed' : null"
        >
          <thead [style.fontSize.px]="headerFontSize">
            <!-- Matrix/Pivot: spanning header groups above the value columns. -->
            <tr *ngIf="hasColumnGroups" class="tm-colgroup-row" [style.background]="headerBackground" [style.color]="headerColor">
              <th *ngIf="hasOwnCheckboxColumn" class="tm-check-cell" [style.backgroundColor]="headerBackground" [style.borderColor]="borderColor"></th>
              <th *ngFor="let col of ungroupedColumns" [style.backgroundColor]="headerBackground" [style.borderColor]="borderColor"></th>
              <th
                *ngFor="let grp of model.columnGroups"
                class="tm-colgroup"
                [attr.colspan]="colGroupSpan(grp)"
                [style.backgroundColor]="headerBackground"
                [style.borderColor]="borderColor"
              >{{ grp.label }}</th>
              <th *ngIf="hasRowActions" class="tm-rowactions-head" [style.backgroundColor]="headerBackground" [style.borderColor]="borderColor"></th>
            </tr>
            <tr
              class="tm-head-row"
              [style.background]="headerBackground"
              [style.color]="headerColor"
            >
              <!-- Row checkboxes in their own column: the header box ticks /
                   clears the whole page. (With a host column picked the
                   checkbox sits inside that column instead - see below.) -->
              <th
                *ngIf="hasOwnCheckboxColumn"
                class="tm-check-cell tm-check-head"
                [class.tm-frozen]="hasFrozenColumns"
                [style.left.px]="hasFrozenColumns ? 0 : null"
                [style.width.px]="checkboxColumnWidth"
                [style.backgroundColor]="headerBackground"
                [style.borderColor]="borderColor"
              >
                <input
                  *ngIf="showSelectAllCheckbox"
                  type="checkbox"
                  [checked]="allPageRowsSelected"
                  [indeterminate]="somePageRowsSelected"
                  (click)="$event.stopPropagation()"
                  (change)="toggleAllRows($event)"
                  title="Select all rows on this page"
                  aria-label="Select all rows on this page"
                />
              </th>
              <th
                *ngFor="let col of visibleColumns; let ci = index"
                [draggable]="enableColumnReorder"
                (dragstart)="onColDragStart(col, $event)"
                (dragover)="onColDragOver(col, $event)"
                (drop)="onColDrop(col, $event)"
                (dragend)="onColDragEnd()"
                [class.tm-col-dragging]="dragColKey === col.key"
                [class.tm-col-dragover]="dragOverColKey === col.key && dragColKey !== col.key"
                [class.tm-col-draggable]="enableColumnReorder"
                [class.tm-frozen]="isColFrozen(col, ci)"
                [class.tm-wrap]="col.cellConfig?.wrap === true"
                [style.backgroundColor]="headerBackground"
                [style.left.px]="isColFrozen(col, ci) ? frozenLeft(ci) : null"
                [style.width.px]="colWidth(col)"
                [attr.data-col-key]="col.key"
                [style.textAlign]="col.align"
                [style.borderColor]="borderColor"
              >
                <!-- Checkbox inside a chosen column: the select-all box leads its header (unless turned off). -->
                <input
                  *ngIf="isCheckboxColumn(col) && showSelectAllCheckbox"
                  type="checkbox"
                  class="tm-check-inline"
                  [checked]="allPageRowsSelected"
                  [indeterminate]="somePageRowsSelected"
                  (click)="$event.stopPropagation()"
                  (change)="toggleAllRows($event)"
                  title="Select all rows on this page"
                  aria-label="Select all rows on this page"
                />
                <span class="tm-th-label" (click)="onSort(col.key, $event)">
                  {{ col.label }}
                  <!-- Mandatory column marker (per-column "Mandatory" checkbox
                       in the builder). aria-hidden + a title on the <th>'s label
                       so screen readers get words, not a bare asterisk. -->
                  <span
                    class="tm-th-required"
                    *ngIf="col.mandatory"
                    aria-hidden="true"
                    title="Required"
                  >*</span>
                  <span class="tm-sort" *ngIf="sortIndicator(col.key)">{{ sortIndicator(col.key) }}</span>
                </span>
                <!-- Column filter trigger (value checklist and / or advanced condition). -->
                <span
                  *ngIf="enableHeaderFilter"
                  class="tm-filter-icon"
                  [class.tm-filter-on]="hasAdvancedFilter(col.key)"
                  (click)="openAdvancedFilter(col.key, $event)"
                  title="Filter this column"
                >⧩</span>
                <!-- Column filter popover. -->
                <div class="tm-filter-pop" [class.tm-filter-pop-values]="enableValueFilter" *ngIf="advancedFilterCol === col.key" (click)="$event.stopPropagation()">
                  <!-- Excel-style: tick the values to keep. -->
                  <ng-container *ngIf="enableValueFilter">
                    <input type="text" class="tm-vf-search" [(ngModel)]="valueFilterSearch" placeholder="Search values…" />
                    <label class="tm-vf-item tm-vf-all" *ngIf="visibleValueOptions.length">
                      <input
                        type="checkbox"
                        [checked]="allVisibleValuesTicked"
                        [indeterminate]="someVisibleValuesTicked"
                        (change)="toggleAllVisibleValues($any($event.target).checked)"
                      />
                      <span class="tm-vf-label">Select all</span>
                    </label>
                    <div class="tm-vf-list">
                      <label class="tm-vf-item" *ngFor="let option of visibleValueOptions; trackBy: trackValueOption">
                        <input
                          type="checkbox"
                          [checked]="isValueTicked(option.value)"
                          (change)="toggleValue(option.value, $any($event.target).checked)"
                        />
                        <span class="tm-vf-label" [class.tm-vf-blank]="option.value === ''" [title]="option.label">{{ option.label }}</span>
                        <span class="tm-vf-count">{{ option.count }}</span>
                      </label>
                      <div class="tm-vf-empty" *ngIf="!visibleValueOptions.length">No matching values</div>
                    </div>
                  </ng-container>
                  <!-- Advanced: operator + typed value. -->
                  <ng-container *ngIf="enableAdvancedFilter">
                    <select [(ngModel)]="advancedFilters[col.key].op">
                      <option *ngFor="let op of advancedFilterOps" [value]="op.value">{{ op.label }}</option>
                    </select>
                    <input *ngIf="advancedFilters[col.key].op !== 'empty' && advancedFilters[col.key].op !== 'notempty'" type="text" [(ngModel)]="advancedFilters[col.key].value" placeholder="Value" />
                    <input *ngIf="advancedFilters[col.key].op === 'between'" type="text" [(ngModel)]="advancedFilters[col.key].value2" placeholder="…and" />
                  </ng-container>
                  <div class="tm-filter-actions">
                    <button type="button" class="tm-btn" (click)="applyAdvancedFilter()">Apply</button>
                    <button type="button" class="tm-btn" (click)="clearAdvancedFilter(col.key)">Clear</button>
                  </div>
                </div>
                <!-- Resize handle. draggable=false + stop dragstart so grabbing
                     the edge resizes instead of starting a column reorder. -->
                <span
                  *ngIf="enableColumnResize"
                  class="tm-resize-handle"
                  draggable="false"
                  (mousedown)="startColumnResize(col, $event)"
                  (dragstart)="$event.preventDefault(); $event.stopPropagation()"
                ></span>
              </th>
              <!-- Trailing header for the row-action buttons: the built-in
                   View / Copy / Delete column carries a label, the Editable
                   type's own row tools keep it blank. -->
              <th *ngIf="hasRowActions" class="tm-rowactions-head" [style.backgroundColor]="headerBackground" [style.borderColor]="borderColor">{{ rowActionsHeader }}</th>
            </tr>
            <!-- Per-column search filter row. -->
            <tr *ngIf="enableColumnSearch && showColumnSearch" class="tm-colsearch-row">
              <th *ngIf="hasOwnCheckboxColumn" class="tm-check-cell" [style.borderColor]="borderColor"></th>
              <th *ngFor="let col of visibleColumns" [style.borderColor]="borderColor">
                <input
                  type="text"
                  class="tm-colsearch-input"
                  [ngModel]="columnSearch[col.key] || ''"
                  (ngModelChange)="columnSearch[col.key] = $event; pageIndex = 0"
                  placeholder="Filter…"
                />
              </th>
              <th *ngIf="hasRowActions" class="tm-rowactions-head" [style.borderColor]="borderColor"></th>
            </tr>
          </thead>
          <tbody>
            <!-- Columns configured but no rows returned yet. -->
            <tr *ngIf="!pagedRows.length" class="tm-nodata-row">
              <td [attr.colspan]="visibleColumns.length + (hasRowActions ? 1 : 0) + (hasOwnCheckboxColumn ? 1 : 0)" [style.borderColor]="borderColor">No data to display.</td>
            </tr>
            <tr
              *ngFor="let row of pagedRows; let i = index; let first = first"
              [class.tm-subtotal]="row.kind === 'subtotal'"
              [class.tm-grandtotal]="row.kind === 'grandtotal'"
              [class.tm-group-row]="row.kind === 'group'"
              [class.tm-total-row]="row.kind === 'total'"
              [class.tm-fin-header]="row.role === 'header'"
              [class.tm-fin-subtotal]="row.role === 'subtotal'"
              [class.tm-fin-total]="row.role === 'total'"
              [class.tm-selected]="row.kind === 'data' && isRowSelected(i)"
              [class.tm-row-frozen]="isRowFrozen(row, dataIndexOf(row))"
              [style.height.px]="rowHeight"
              [style.background]="(row.kind !== 'data' && row.kind !== 'group') ? totalRowBackground : null"
              (click)="onRowClick(row, i)"
            >
              <!-- Row checkbox in its own column (data rows only; total / group rows get an empty cell). -->
              <td
                *ngIf="hasOwnCheckboxColumn"
                class="tm-check-cell"
                [class.tm-frozen]="hasFrozenColumns"
                [style.left.px]="hasFrozenColumns ? 0 : null"
                [style.width.px]="checkboxColumnWidth"
                [style.background]="hasFrozenColumns ? '#fff' : null"
                [style.borderColor]="borderColor"
                (click)="$event.stopPropagation()"
              >
                <input
                  *ngIf="row.kind === 'data'"
                  type="checkbox"
                  [checked]="isRowSelected(i)"
                  (change)="toggleRowCheckbox(i, $event)"
                  [attr.aria-label]="'Select row ' + (i + 1)"
                />
              </td>
              <!-- Only a NAVIGABLE cell carries a native tooltip (it explains
                   that clicking navigates). Ordinary cells carry none: the old
                   fallback echoed the cell's own display value, so hovering any
                   cell popped up the number already printed in it — and on an
                   editable cell that tooltip sat right under the input you were
                   typing into. -->
              <td
                *ngFor="let col of visibleColumns; let firstCol = first; let ci = index"
                [class.tm-frozen]="isColFrozen(col, ci)"
                [class.tm-navigable]="row.kind === 'data' && (isNavigableColumn(col.key) || hasCellNavigation(col))"
                [class.tm-nav-selected]="isNavSelected(i, col.key)"
                [class.tm-wrap]="col.cellConfig?.wrap === true"
                [style.textAlign]="col.align"
                [style.width.px]="colWidth(col)"
                [style.left.px]="isColFrozen(col, ci) ? frozenLeft(ci) : null"
                [style.top.px]="isRowFrozen(row, dataIndexOf(row)) ? frozenRowTop(dataIndexOf(row)) : null"
                [style.background]="row.cells[col.key]?.background || (isRowFrozen(row, dataIndexOf(row)) ? '#fff' : (isColFrozen(col, ci) ? '#fff' : null))"
                [style.color]="row.cells[col.key]?.color || null"
                [style.fontWeight]="row.cells[col.key]?.fontWeight || null"
                [style.fontStyle]="row.cells[col.key]?.fontStyle || null"
                [class.tm-cell-highlight]="row.cells[col.key]?.highlight === true"
                [style.borderColor]="borderColor"
                [style.paddingLeft.px]="firstCol && (isExpandable || isFinancial) ? (8 + rowIndent(row)) : null"
                [attr.title]="row.cells[col.key]?.tooltip || (row.kind === 'data' && hasCellNavigation(col) ? cellNavTitle(col) : (row.kind === 'data' && isNavigableColumn(col.key) ? navCellTitle : null))"
                (click)="onNavCellClick(row, i, col.key, $event)"
                (dblclick)="copyCell(row.cells[col.key]?.display || '')"
              >
                <!-- Row checkbox INSIDE the chosen column (rowCheckboxColumn), before the value. -->
                <input
                  *ngIf="row.kind === 'data' && isCheckboxColumn(col)"
                  type="checkbox"
                  class="tm-check-inline"
                  [checked]="isRowSelected(i)"
                  (click)="$event.stopPropagation()"
                  (change)="toggleRowCheckbox(i, $event)"
                  [attr.aria-label]="'Select row ' + (i + 1)"
                />
                <!-- Grouped/Tree: expand/collapse chevron on the first column. -->
                <span
                  *ngIf="firstCol && isExpandable && row.expandable"
                  class="tm-expander"
                  (click)="toggleExpand(row, $event)"
                >{{ isRowExpanded(row) ? '▾' : '▸' }}</span>
                <!-- ACTION column: a button in every DATA row, independent of
                     table type and edit mode (checked before the editable-cell
                     branch, which would otherwise swallow it). Subtotal/total
                     rows get nothing — there is no record to act on. -->
                <ng-container *ngIf="col.isAction; else notActionCell">
                  <!-- popupComponent kind: render the shared popup widget itself,
                       so the cell gets its full block set (heading / text /
                       key-value / table / apiTable / buttons / divider) and its
                       own body-level dialog — no second modal implementation.
                       It draws its own trigger, which is why our button below is
                       skipped for this kind. -->
                  <app-dvm-popup
                    *ngIf="row.kind === 'data' && col.actionKind === 'popupComponent'"
                    class="tm-action-popup"
                    [data]="rowPopupData(row, col)"
                  ></app-dvm-popup>
                  <!-- buttons kind: several buttons, each running its own feature for this row. -->
                  <span class="tm-cell-buttons" *ngIf="row.kind === 'data' && col.actionKind === 'buttons'">
                    <ng-container *ngFor="let button of col.cellButtons ?? []">
                      <button *ngIf="!isActionButtonHidden(col, button.label)" type="button"
                        class="tm-cell-button" [attr.data-tone]="button.tone || 'blue'" [class.is-filled]="button.variant === 'filled'"
                        (click)="onCellButtonClick(row, col, button, $event)">{{ button.label }}</button>
                    </ng-container>
                  </span>
                  <ng-container *ngIf="row.kind === 'data' && col.actionKind !== 'popupComponent' && col.actionKind !== 'buttons'">
                    <!-- A case with several buttons (Approve + Reject) draws them side by side.
                         Hidden ones are skipped in place so `i` still names the configured button. -->
                    <div class="tm-action-group" *ngIf="actionCaseButtons(row, col) as buttons; else singleAction">
                      <ng-container *ngFor="let btn of buttons; let i = index; trackBy: trackByIndex">
                      <button
                        *ngIf="!isActionButtonHidden(col, btn.label)"
                        type="button"
                        class="tm-btn tm-action-btn"
                        [class.is-link]="btn.style === 'link'"
                        [ngClass]="btn.tone ? 'tone-' + btn.tone : ''"
                        [class.is-busy]="isActionBusy(row, col)"
                        [disabled]="isActionBusy(row, col)"
                        [attr.aria-busy]="isActionBusy(row, col) ? 'true' : null"
                        [title]="btn.label || actionTooltip(col)"
                        [attr.aria-label]="btn.label || actionTooltip(col)"
                        (click)="onActionClick(row, col, $event, i)"
                      >
                        <span class="tm-action-label">{{ btn.label }}</span>
                      </button>
                      </ng-container>
                    </div>
                    <ng-template #singleAction>
                    <ng-container *ngIf="actionCase(row, col) as action">
                      <!-- actionByValue "none": this row has no action (e.g. already approved). -->
                      <span class="tm-action-none" *ngIf="action.style === 'none'">—</span>
                      <button
                        *ngIf="action.style !== 'none'"
                        type="button"
                        class="tm-btn tm-action-btn"
                        [class.is-link]="action.style === 'link'"
                        [ngClass]="action.tone ? 'tone-' + action.tone : ''"
                        [class.is-busy]="isActionBusy(row, col)"
                        [disabled]="isActionBusy(row, col)"
                        [attr.aria-busy]="isActionBusy(row, col) ? 'true' : null"
                        [title]="action.label || actionTooltip(col)"
                        [attr.aria-label]="action.label || actionTooltip(col)"
                        (click)="onActionClick(row, col, $event)"
                      >
                        <span class="tm-action-spinner" *ngIf="isActionBusy(row, col)" aria-hidden="true"></span>
                        <ng-container *ngIf="!isActionBusy(row, col)">
                          <mat-icon *ngIf="isMaterialIcon(col.buttonIcon)">{{ col.buttonIcon }}</mat-icon>
                          <span class="tm-action-glyph" *ngIf="col.buttonIcon && !isMaterialIcon(col.buttonIcon)">{{ col.buttonIcon }}</span>
                        </ng-container>
                        <!-- Icon-only stays icon-only: the label renders only when
                             configured, so a compact action column stays compact. -->
                        <span class="tm-action-label" *ngIf="action.label">{{ action.label }}</span>
                      </button>
                    </ng-container>
                    </ng-template>
                  </ng-container>
                </ng-container>

                <ng-template #notActionCell>
                <!-- Editable data cell with an inline input control. In row-edit
                     mode only the row currently open shows inputs. -->
                <ng-container *ngIf="isEditable && row.kind === 'data' && col.control && !isRowDeleted(row) && isRowInEdit(row); else plainCell">
                  <input
                    *ngIf="col.control === 'number' || col.control === 'currency' || col.control === 'percent'"
                    type="text"
                    class="tm-cell-input"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    [min]="col.min ?? null"
                    [max]="col.max ?? null"
                    inputmode="decimal"
                    autocomplete="off"
                    [ngModel]="row.cells[col.key]?.value"
                    (keydown)="onNumericCellKeydown($event)"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  />
                  <input
                    *ngIf="col.control === 'text'"
                    type="text"
                    class="tm-cell-input"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  />
                  <textarea
                    *ngIf="col.control === 'textarea'"
                    class="tm-cell-input tm-cell-textarea"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    rows="1"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  ></textarea>
                  <input
                    *ngIf="col.control === 'date'"
                    type="date"
                    class="tm-cell-input"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  />
                  <input
                    *ngIf="col.control === 'checkbox'"
                    type="checkbox"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  />
                  <select
                    *ngIf="col.control === 'dropdown'"
                    class="tm-cell-input"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  >
                    <option value="">{{ col.cellConfig?.placeholder || '—' }}</option>
                    <ng-container *ngIf="col.options?.length; else currentOnly">
                      <option *ngFor="let opt of col.options" [value]="opt.value">{{ opt.label }}</option>
                    </ng-container>
                    <ng-template #currentOnly>
                      <option [value]="row.cells[col.key]?.value">{{ row.cells[col.key]?.display }}</option>
                    </ng-template>
                  </select>
                  <!-- ── Rich controls (multiselect/toggle/datetime/file/lookup/autocomplete/radio/button/hyperlink) ── -->
                  <select
                    *ngIf="col.control === 'multiselect'"
                    class="tm-cell-input" multiple
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngModel]="cellMultiValue(row, col.key)"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  >
                    <option *ngFor="let opt of col.options" [value]="opt.value">{{ opt.label }}</option>
                  </select>
                  <label *ngIf="col.control === 'toggle'" class="tm-toggle">
                    <input type="checkbox" [ngModel]="!!row.cells[col.key]?.value" (ngModelChange)="onCellEdit(row, col.key, $event)" />
                    <span class="tm-toggle-track"></span>
                  </label>
                  <input
                    *ngIf="col.control === 'datetime'"
                    type="datetime-local"
                    class="tm-cell-input"
                    [class.tm-cell-invalid]="cellError(row, col.key)"
                    [ngStyle]="cellInputStyle(row, col)"
                    [ngModel]="row.cells[col.key]?.value"
                    (ngModelChange)="onCellEdit(row, col.key, $event)"
                  />
                  <input
                    *ngIf="col.control === 'file'"
                    type="file"
                    class="tm-cell-input tm-cell-file"
                    (change)="onCellFile(row, col.key, $event)"
                  />
                  <!-- Lookup + Autocomplete: text input backed by a datalist of
                       config options (fetched live from lookupUrl when set). -->
                  <ng-container *ngIf="col.control === 'lookup' || col.control === 'autocomplete'">
                    <input
                      type="text"
                      class="tm-cell-input"
                      [class.tm-cell-invalid]="cellError(row, col.key)"
                      [attr.list]="'tm-lk-' + col.key"
                      [ngModel]="row.cells[col.key]?.value"
                      (ngModelChange)="onCellEdit(row, col.key, $event)"
                      (focus)="ensureLookupOptions(col)"
                      (input)="onLookupInput(col, $event)"
                    />
                    <datalist [id]="'tm-lk-' + col.key">
                      <option *ngFor="let opt of lookupOptions(col)" [value]="opt.value">{{ opt.label }}</option>
                    </datalist>
                  </ng-container>
                  <div *ngIf="col.control === 'radio'" class="tm-radio-group">
                    <label *ngFor="let opt of col.options" class="tm-radio">
                      <input type="radio" [name]="col.key + '-' + row.rowId" [value]="opt.value"
                             [ngModel]="row.cells[col.key]?.value" (ngModelChange)="onCellEdit(row, col.key, $event)" />
                      {{ opt.label }}
                    </label>
                  </div>
                  <button *ngIf="col.control === 'button'" type="button" class="tm-btn tm-cell-btn" (click)="onCellActionClick(row, col, $event)">
                    {{ col.buttonLabel || col.label }}
                  </button>
                  <a *ngIf="col.control === 'hyperlink'" class="tm-cell-link" [tableCellNavigation]="col.cellConfig" [navigationRow]="row.raw" [navigationField]="col.key" [navigationValue]="row.cells[col.key]?.value" [navigationDefaultKey]="navDefaultKey(col)" [navigationHref]="cellHref(row, col)" target="_blank" rel="noopener"
                     (click)="onCellActionClick(row, col, $event)">{{ row.cells[col.key]?.display || row.cells[col.key]?.value }}</a>
                  <!-- Inline validation error under the control. -->
                  <span class="tm-cell-error" *ngIf="cellError(row, col.key)">{{ cellError(row, col.key) }}</span>
                </ng-container>
                <ng-template #plainCell>
                  <!-- GENERIC cell display (table-cell-config.ts): the look was
                       resolved onto the cell by the pure builder; here only
                       the SHAPE varies — badge / data bar / progress / trend /
                       icon dot / link — else the plain value. Total / group
                       rows carry no decoration (the builder leaves them plain). -->
                  <ng-container [ngSwitch]="row.kind === 'data' ? (col.cellConfig?.display || 'text') : 'text'">
                    <span *ngSwitchCase="'badge'" class="tm-cell-badge" [ngStyle]="badgeStyle(row.cells[col.key])" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-icon" *ngIf="row.cells[col.key]?.icon">{{ row.cells[col.key]?.icon }}</span>{{ row.cells[col.key]?.label }}
                    </span>
                    <span *ngSwitchCase="'databar'" class="tm-cell-databar-wrap" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-databar" [style.width.%]="row.cells[col.key]?.percent || 0" [style.background]="row.cells[col.key]?.accent"></span>
                      <span class="tm-cell-databar-val">{{ row.cells[col.key]?.display }}</span>
                    </span>
                    <span *ngSwitchCase="'progress'" class="tm-cell-progress" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-progress-fill" [style.width.%]="row.cells[col.key]?.percent || 0" [style.background]="row.cells[col.key]?.accent"></span>
                      <span class="tm-cell-progress-label">{{ row.cells[col.key]?.display }}</span>
                    </span>
                    <span *ngSwitchCase="'trend'" class="tm-cell-trend" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-icon">{{ row.cells[col.key]?.icon }}</span>{{ row.cells[col.key]?.display }}
                    </span>
                    <span *ngSwitchCase="'icon'" class="tm-cell-iconcell" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-dot" [style.background]="row.cells[col.key]?.accent"></span>
                      <span class="tm-cell-icon" *ngIf="row.cells[col.key]?.icon">{{ row.cells[col.key]?.icon }}</span>{{ row.cells[col.key]?.display }}
                    </span>
                    <span *ngSwitchCase="'buttons'" class="tm-cell-buttons">
                      <button *ngFor="let button of col.cellConfig?.buttons ?? []" type="button"
                        class="tm-cell-button" [attr.data-tone]="button.tone || 'blue'" [class.is-filled]="button.variant === 'filled'"
                        (click)="onCellButtonClick(row, col, button, $event)">{{ button.label }}</button>
                    </span>
                    <a *ngSwitchCase="'link'" class="tm-cell-link" [tableCellNavigation]="col.cellConfig" [navigationRow]="row.raw" [navigationField]="col.key" [navigationValue]="row.cells[col.key]?.value" [navigationDefaultKey]="navDefaultKey(col)" [navigationHref]="row.cells[col.key]?.href || '#'" target="_blank" rel="noopener" (click)="$event.stopPropagation()" [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-icon" *ngIf="row.cells[col.key]?.icon">{{ row.cells[col.key]?.icon }}</span>{{ row.cells[col.key]?.display }}
                    </a>
                    <span *ngSwitchDefault [class.tm-cell-deleted]="isRowDeleted(row)">
                      <span class="tm-cell-icon" *ngIf="row.cells[col.key]?.icon">{{ row.cells[col.key]?.icon }}</span>{{ row.cells[col.key]?.display }}
                    </span>
                  </ng-container>
                  <!-- Row-state badge on the first column. -->
                  <span *ngIf="firstCol && showRowState && rowState(row)" class="tm-state-badge" [ngClass]="'tm-state-' + rowState(row)">{{ rowState(row) }}</span>
                </ng-template>
                </ng-template><!-- /notActionCell -->
              </td>
              <!-- Editable: trailing row-action buttons. -->
              <td *ngIf="hasRowActions && row.kind === 'data'" class="tm-rowactions" [style.borderColor]="borderColor" (click)="$event.stopPropagation()">
                <!-- Built-in row actions (any table type): View / Copy / Save /
                     Delete, always visible. Save and Delete go through the CRUD
                     endpoints when configured. When one is on, the Editable
                     type's own tool for the same job is not repeated. -->
                <button *ngIf="rowActionView" type="button" class="tm-rowbtn tm-rowbtn-action tm-rowbtn-view" (click)="viewRow(row, $event)" title="View row" aria-label="View row"><mat-icon>visibility</mat-icon></button>
                <button *ngIf="rowActionCopy && !isRowDeleted(row)" type="button" class="tm-rowbtn tm-rowbtn-action tm-rowbtn-copy" (click)="copyRowAction(row, $event)" title="Copy row" aria-label="Copy row"><mat-icon>content_copy</mat-icon></button>
                <button *ngIf="rowActionSave" type="button" class="tm-rowbtn tm-rowbtn-action tm-rowbtn-save" [disabled]="!canSaveRowAction(row)" (click)="saveRowAction(row, $event)" [title]="isSaving(row) ? 'Saving…' : (canSaveRowAction(row) ? 'Save row' : 'No unsaved changes')" aria-label="Save row"><mat-icon>{{ isSaving(row) ? 'hourglass_top' : 'save' }}</mat-icon></button>
                <button *ngIf="rowActionDelete && !isRowDeleted(row)" type="button" class="tm-rowbtn tm-rowbtn-action tm-rowbtn-del" (click)="deleteRowAction(row, $event)" title="Delete row" aria-label="Delete row"><mat-icon>delete_outline</mat-icon></button>
                <button *ngIf="rowActionDelete && isRowDeleted(row)" type="button" class="tm-rowbtn tm-rowbtn-action" (click)="restoreRow(row)" title="Restore row" aria-label="Restore row"><mat-icon>undo</mat-icon></button>
                <!-- Row-edit mode: open / done toggle. -->
                <button *ngIf="isRowEditMode && !isRowInEdit(row)" type="button" class="tm-rowbtn" (click)="beginRowEdit(row, $event)" title="Edit row">✎</button>
                <button *ngIf="isRowEditMode && isRowInEdit(row)" type="button" class="tm-rowbtn" (click)="endRowEdit(row)" title="Done editing">✓</button>
                <button *ngIf="enableRowInsert" type="button" class="tm-rowbtn" (click)="insertRow(row, 'above')" title="Insert row above">⤒</button>
                <button *ngIf="enableRowInsert" type="button" class="tm-rowbtn" (click)="insertRow(row, 'below')" title="Insert row below">⤓</button>
                <button *ngIf="enableRowDuplicate && !rowActionCopy" type="button" class="tm-rowbtn" (click)="duplicateRow(row)" title="Duplicate row">⧉</button>
                <button *ngIf="enableCopyPaste" type="button" class="tm-rowbtn" (click)="copyRow(row)" title="Copy row">⧉c</button>
                <button *ngIf="enableCopyPaste" type="button" class="tm-rowbtn" (click)="pasteRow(row)" title="Paste into row">⇲</button>
                <button *ngIf="enableRowSave && !rowActionSave" type="button" class="tm-rowbtn tm-rowbtn-save" [disabled]="isSaving(row)" (click)="saveRow(row)" title="Save this row">{{ isSaving(row) ? '⏳' : '💾' }}</button>
                <button *ngIf="enableRowDelete && !rowActionDelete && !isRowDeleted(row)" type="button" class="tm-rowbtn tm-rowbtn-del" (click)="deleteRow(row)" title="Delete row">🗑</button>
                <button *ngIf="enableRowDelete && !rowActionDelete && isRowDeleted(row)" type="button" class="tm-rowbtn" (click)="restoreRow(row)" title="Restore row">↺</button>
              </td>
              <td *ngIf="hasRowActions && row.kind !== 'data'" class="tm-rowactions" [style.borderColor]="borderColor"></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="tm-pager" *ngIf="pageSize && totalPages > 1">
        <button type="button" class="tm-btn" (click)="prevPage()" [disabled]="pageIndex === 0">‹ Prev</button>
        <span class="tm-page-info">Page {{ pageIndex + 1 }} / {{ totalPages }}</span>
        <button type="button" class="tm-btn" (click)="nextPage()" [disabled]="pageIndex >= totalPages - 1">Next ›</button>
      </div>
    </ng-container>
  </div>
  <div
    *ngIf="saveNotice"
    class="tm-save-notice"
    [class.tm-save-notice-error]="saveNoticeTone === 'error'"
  >
    {{ saveNotice }}
  </div>
</div>

<!-- Action-column modal (rowPopup = this row's fields; apiPopup = the response).
     Fixed-position backdrop so it centres over the viewport rather than being
     trapped inside the tile's scroll box. -->
<div #actionOverlayRoot class="tm-action-backdrop" *ngIf="actionModal" (click)="closeActionModal()">
  <div
    class="tm-action-dialog"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="actionModal.title"
    (click)="$event.stopPropagation()"
  >
    <header class="tm-action-head">
      <h3>{{ actionModal.title }}</h3>
      <button type="button" class="tm-action-close" aria-label="Close" (click)="closeActionModal()">✕</button>
    </header>

    <div class="tm-action-body">
      <div class="tm-action-state" *ngIf="actionModal.loading">
        <span class="tm-action-spinner" aria-hidden="true"></span> Loading…
      </div>
      <div class="tm-action-state is-error" *ngIf="!actionModal.loading && actionModal.error">{{ actionModal.error }}</div>

      <ng-container *ngIf="!actionModal.loading && !actionModal.error">
        <!-- rowPopup: label / value pairs. -->
        <dl class="tm-action-pairs" *ngIf="actionModal.pairs.length">
          <div class="tm-action-pair" *ngFor="let pair of actionModal.pairs">
            <dt>{{ pair.label }}</dt>
            <dd>{{ pair.value || '—' }}</dd>
          </div>
        </dl>

        <!-- apiPopup: the response rendered as a table. -->
        <div class="tm-action-table-wrap" *ngIf="actionModal.rows.length">
          <table class="tm-action-table">
            <thead>
              <tr><th *ngFor="let col of actionModal.rowColumns">{{ col.label }}</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of actionModal.rows">
                <td *ngFor="let col of actionModal.rowColumns">{{ actionCellText(r[col.key]) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="tm-action-state" *ngIf="!actionModal.pairs.length && !actionModal.rows.length">No data to display.</div>
      </ng-container>
    </div>
  </div>
</div>
